
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTeacherDetails } from '@/hooks/useTeacherDetails';
import { printTeacherProfile } from '@/utils/printing/teacher';
import { TeacherPaymentHistory } from '@/components/teachers/TeacherPaymentHistory';
import { TeacherProfileCard } from '@/components/teachers/details/TeacherProfileCard';
import { TeacherAboutTab } from '@/components/teachers/details/TeacherAboutTab';
import { TeacherScheduleTab } from '@/components/teachers/details/TeacherScheduleTab';
import { TeacherDocumentsTab } from '@/components/teachers/details/TeacherDocumentsTab';
import { TeacherSalaryTab } from '@/components/teachers/details/TeacherSalaryTab';
import { TeacherPaymentsTab } from '@/components/teachers/details/TeacherPaymentsTab';
import { TeacherHeaderActions } from '@/components/teachers/details/TeacherHeaderActions';
import { TeacherLoadingState } from '@/components/teachers/details/TeacherLoadingState';
import { TeacherNotFoundState } from '@/components/teachers/details/TeacherNotFoundState';
import { TeacherOfflineIndicator } from '@/components/teachers/TeacherOfflineIndicator';
import { Helmet } from 'react-helmet-async';

const TeacherDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { teacher, isLoading, deleteTeacher } = useTeacherDetails(id);
  const [currentTab, setCurrentTab] = useState("about");

  useEffect(() => {
    // Reset to default tab when teacher changes
    setCurrentTab("about");
  }, [id]);

  const handleDelete = async () => {
    if (!teacher) return;
    
    if (window.confirm(`Are you sure you want to delete ${teacher.firstName} ${teacher.lastName}?`)) {
      try {
        await deleteTeacher();
        // Navigation is handled in the hook
      } catch (error) {
        console.error("Error deleting teacher:", error);
      }
    }
  };

  const handlePrintProfile = async (format: 'jpg' | 'pdf') => {
    if (teacher) {
      const { printTeacherProfileAsJPG, printTeacherProfileAsPDF } = await import('@/utils/printing/teacher');
      
      if (format === 'jpg') {
        await printTeacherProfileAsJPG(teacher);
      } else {
        await printTeacherProfileAsPDF(teacher);
      }
    }
  };

  if (isLoading) {
    return <TeacherLoadingState />;
  }

  if (!teacher) {
    return <TeacherNotFoundState />;
  }

  const teacherName = `${teacher.firstName} ${teacher.lastName}`;

  return (
    <div className="container mx-auto px-4 py-6">
      <Helmet>
        <title>{teacherName} - Teacher Details - NALC Student Program & Fee Tracker</title>
      </Helmet>
      
      <TeacherOfflineIndicator resource="details" teacherId={id} />
      
      <TeacherHeaderActions 
        id={id || ''} 
        teacherName={teacherName}
        onDelete={handleDelete} 
        onPrint={handlePrintProfile} 
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <TeacherProfileCard teacher={teacher} />

        <Card className="p-6 col-span-1 lg:col-span-2">
          <Tabs value={currentTab} onValueChange={setCurrentTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="about">About</TabsTrigger>
              <TabsTrigger value="schedule">Schedule</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
              <TabsTrigger value="salary">Salary</TabsTrigger>
              <TabsTrigger value="payments">Payments</TabsTrigger>
            </TabsList>
            
            <TabsContent value="about">
              <TeacherAboutTab teacher={teacher} />
            </TabsContent>
            
            <TabsContent value="schedule">
              <TeacherScheduleTab teacher={teacher} />
            </TabsContent>
            
            <TabsContent value="documents">
              <TeacherDocumentsTab teacher={teacher} />
            </TabsContent>
            
            <TabsContent value="salary">
              <TeacherSalaryTab teacher={teacher} teacherId={id || ''} />
            </TabsContent>
            
            <TabsContent value="payments">
              <TeacherPaymentsTab teacherId={id || ''} />
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

export default TeacherDetails;
